'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["DescriptionListComponent", "OperatorDescriptionComponent", "addConditionDisabledTooltip", "bannerComponents", "baseFilterFamily", "className", "constraint", "currencyCode", "customRestrictedFiltersViewState", "displayMode", "editorSubtitle", "getActiveFieldNames", "getFieldDefinitions", "getFieldGroups", "getFields", "getFilterFamilyAndButtonTooltip", "getFilterFamilyGroupHeading", "getFilterFamilyHasEntities", "getFilterFamilyName", "getFilterFamilyObjectName", "getFilterFamilyOptions", "getInitialConditionCountCutoff", "getIsUngated", "getObjectPropertyLabel", "getReferenceResolvers", "isAddConditionDisabled", "isFieldDescriptionVisible", "isFiscalYearEnabled", "isInitialScreenCreate", "isReadOnly", "isRollingDateOffsetInputEnabled", "isXoEnabled", "labelOperatorCreateSave", "labelOperatorEditSave", "matchStatus", "onChangeFilterFamily", "onFieldSearchChange", "onTempColumnAdd", "onUserAction", "searchable", "showIncludeUnknownValues", "showInvalidOptionErrors", "showInitialStepBackButton", "style", "validateOperator", "value", "valueBase", "getObjectAssociationOptions", "isAssociationSelectEnabled"];
import * as DisplayTypes from 'customer-data-filters/filterQueryFormat/DisplayTypes';
import * as ExternalOptionTypes from 'customer-data-objects/property/ExternalOptionTypes';
import * as FieldTranslator from './FieldTranslator';
import * as LogicGroup from 'customer-data-filters/filterQueryFormat/logic/LogicGroup';
import * as PropertyDisplay from 'customer-data-property-utils/PropertyDisplay';
import { CONDITION_ADDED, CONDITION_CLONED, CONDITION_DELETED, CONDITION_DRAFT_ABANDONED, CONDITION_DRAFT_CHANGED, CONDITION_DRAFT_SAVED, SHOW_ALL_CONDITIONS_CLICKED } from 'customer-data-filters/filterQueryFormat/UsageSubActionTypes';
import { DESCRIPTION, EDITOR } from 'customer-data-filters/filterQueryFormat/DisplayMode';
import { EMAIL_SUBSCRIPTION, SITE_CONTENT, SURVEY_QUESTION } from 'reference-resolvers/constants/ReferenceObjectTypes';
import { FilterFamilyAndButtonTooltipTranslator, FilterFamilyGroupHeadingTranslator, FilterFamilyNameTranslator, LegacyFilterFamilyObjectNameTranslator } from './FilterFamilyTranslator';
import { List, Map as ImmutableMap, OrderedSet, Set as ImmutableSet, is } from 'immutable';
import { NotWildCardEqual, WildCardEqual } from 'customer-data-filters/filterQueryFormat/operator/Operators';
import { getObjectSegOperatorsForTypeStrict } from 'customer-data-filters/converters/objectSeg/ObjectSegTypeToOperator';
import { getOperatorLabelTranslationKey } from './operator/FilterOperatorLabelTranslations';
import Constraint from '../filterQueryFormat/logic/Constraint';
import DefaultFieldSelectItemComponent from './editor/FieldSelectItemComponent';
import FieldLink from './editor/FieldLink';
import FilterEditorOperatorCreate from './editor/FilterEditorOperatorCreate';
import FilterEditorOperatorDescriptionList from './description/FilterEditorOperatorDescriptionList';
import FilterEditorOperatorDisplayList from './FilterEditorOperatorDisplayList';
import FilterEditorOperatorEdit from './editor/FilterEditorOperatorEdit';
import FilterOperatorBoolInput from './operator/FilterOperatorBoolInput';
import FilterOperatorDateInput from './operator/FilterOperatorDateInput';
import FilterOperatorDateInputMoment from './operator/FilterOperatorDateInputMoment';
import FilterOperatorDurationInput from './operator/FilterOperatorDurationInput';
import FilterOperatorElasticsearchTextQueryInput from './operator/FilterOperatorElasticsearchTextQueryInput';
import FilterOperatorEmailSubscriptionInput from './operator/FilterOperatorEmailSubscriptionInput';
import FilterOperatorEnumInput from './operator/FilterOperatorEnumInput';
import FilterOperatorErrorRecord from 'customer-data-filters/filterQueryFormat/FilterOperatorErrorRecord';
import FilterOperatorMultiStringInput from './operator/FilterOperatorMultiStringInput';
import FilterOperatorNumberInput from './operator/FilterOperatorNumberInput';
import FilterOperatorOwnerInput from './operator/FilterOperatorOwnerInput';
import FilterOperatorPercentageInput from './operator/FilterOperatorPercentageInput';
import FilterOperatorTeamInput from './operator/FilterOperatorTeamInput';
import FilterOperatorTextInput from './operator/FilterOperatorTextInput';
import FilterType from 'customer-data-filters/components/propTypes/FilterType';
import FilterUserAction from '../filterQueryFormat/FilterUserAction';
import MatchStatusType from './propTypes/MatchStatusType';
import Or from 'customer-data-filters/filterQueryFormat/logic/Or';
import PropTypes from 'prop-types';
import Restricted from '../filterQueryFormat/logic/Restricted';
import RestrictedFiltersViewState from './editor/RestrictedFiltersViewState';
import { Component } from 'react';
import { syntheticEvent } from 'foundations-components/transitional/utils/SyntheticEvent';
import UIList from 'UIComponents/list/UIList';
import always from 'transmute/always';
import flatten from 'transmute/flatten';
import get from 'transmute/get';
import getIn from 'transmute/getIn';
import getSpecialOptionsByReferenceType from './getSpecialOptionsByReferenceType';
import hasIn from 'transmute/hasIn';
import identity from 'transmute/identity';
import invariant from 'react-utils/invariant';
import map from 'transmute/map';
import memoize from 'transmute/memoize';
import partial from 'transmute/partial';
import pipe from 'transmute/pipe';
import unescapedText from 'I18n/utils/unescapedText';
import valueSeq from 'transmute/valueSeq';
import { parseAssociationOption } from './strategies/ObjectSegStrategyUtils';
import { isExternalOptionsField } from '../utilities/isExternalOptionsField';
import { operatorSupportsExternalOptions } from '../utilities/operatorSupportsExternalOptions';
import { getPropertyResolver } from 'reference-resolvers-lite/utils/getPropertyResolver';
import { ObjectTypesToIds } from 'customer-data-objects/constants/ObjectTypeIds';
import { conditionsGrouping } from '../utilities/addingCondition';
import * as UnifiedEventPropertyTypes from 'customer-data-filters/filterQueryFormat/UnifiedEventPropertyTypes';
import * as PropertyTypes from 'customer-data-objects/property/PropertyTypes';
import I18n from 'I18n';
import MissingField from '../filterQueryFormat/MissingField';
import { DATE_TIME } from 'customer-data-objects/property/PropertyTypes';
import { RangeInputType } from '../filterQueryFormat/InputTypes';
import FilterOperatorDateRangeInputMoment from './operator/FilterOperatorDateRangeInputMoment';
import FilterOperatorDateRangeInput from './operator/FilterOperatorDateRangeInput';
import { shouldUseEnumComponentsForExternalOptionsField, isOperatorIncompatibleWithExternalOptionsField, attemptToConvertExternalOptionsFilter } from '../utilities/externalOptionsConversionUtils';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
const _getActiveFieldNamesFromGroup = logicGroup => {
  const recurse = pipe(get('conditions'), valueSeq, map(_getActiveFieldNamesFromGroup), ImmutableSet, flatten);
  return !hasIn(['field', 'name'], logicGroup) ? ImmutableSet() : getIn(['field', 'name'], logicGroup) || recurse(logicGroup);
};
const PANEL_KEYS = {
  CREATE: 'CREATE',
  EDIT: 'EDIT',
  LIST: 'LIST'
};
const _isImmutableEqual = (a, b) => Object.keys(a).some(key => {
  if (key === 'style') {
    if (!is(ImmutableMap(a[key]), ImmutableMap(b[key]))) {
      return true;
    }
  }
  if (!is(a[key], b[key])) {
    return true;
  }
  return false;
});
export const WEBPACK_3_FORCE_MODULE_IMPORT = 1;
export default class XOFilterEditor extends Component {
  constructor(_props) {
    super(_props);
    this.updateNullIssueRelatedState = newState => {
      if (newState) {
        this.setState({
          nullIssueRelatedState: newState
        });
      }
    };
    this.getDerivedState = (props, prevState = {}) => {
      const {
        getFilterFamilyOptions = () => OrderedSet(),
        isInitialScreenCreate,
        isXoEnabled,
        value
      } = props;
      let state = Object.assign({
        filterFamily: undefined,
        getGroup: identity,
        panelKey: PANEL_KEYS.LIST
      }, prevState);
      if (state.panelKey === PANEL_KEYS.LIST && state.panelKey !== prevState.panelKey && isInitialScreenCreate) {
        state.panelKey = PANEL_KEYS.CREATE;
      }
      if (state.panelKey === PANEL_KEYS.LIST && prevState.isInitialScreenCreate) {
        state.didScheduleStepInitializationCheck = true;
      }
      if (state.panelKey === PANEL_KEYS.CREATE && prevState.panelKey === PANEL_KEYS.LIST) {
        state.didScheduleStepInitializationCheck = false;
      }
      if (state.panelKey === PANEL_KEYS.CREATE) {
        const filterFamilyOptions = getFilterFamilyOptions() || OrderedSet();
        const initialFilterFamily = filterFamilyOptions.size <= 1 ? filterFamilyOptions.first() : undefined;
        const creationStateStaticAttributes = {
          draftCondition: null,
          filterFamily: isXoEnabled ? state.filterFamily || initialFilterFamily : state.filterFamily,
          initialFilterFamily,
          originalValue: value,
          draftValue: value
        };
        const creationStateDefaultAttributes = {
          getGroup: identity,
          applyDraftToGroup: partial(LogicGroup.addToKeyPath, List())
        };
        state = Object.assign({}, creationStateDefaultAttributes, state, creationStateStaticAttributes);
      }
      if (state.panelKey === PANEL_KEYS.EDIT) {
        const editingStateStaticAttributes = {
          originalValue: value,
          draftValue: value
        };
        state = Object.assign({}, state, editingStateStaticAttributes);
      }
      return state;
    };
    this.getInputComponent = operator => {
      const field = operator.field;
      const {
        getFieldDefinitions,
        getOperatorsForType,
        getIsUngated
      } = this.props;
      const {
        filterFamily,
        draftCondition
      } = this.state;
      const fieldDefinitions = getFieldDefinitions(filterFamily);
      const referencedObjectType = this.getReferencedObjectType(filterFamily, operator);
      const fieldDefinitionInputComponent = getIn([field.name, 'InputComponent'], fieldDefinitions);
      if (field.type === 'number' && field.numberDisplayHint === 'duration') {
        return FilterOperatorDurationInput;
      }
      if (field.type === 'number' && field.numberDisplayHint === 'percentage') {
        return FilterOperatorPercentageInput;
      }
      if (referencedObjectType === ExternalOptionTypes.TEAM) {
        return FilterOperatorTeamInput;
      }
      if (fieldDefinitionInputComponent) {
        return fieldDefinitionInputComponent;
      }
      if (referencedObjectType === ExternalOptionTypes.OWNER) {
        return FilterOperatorOwnerInput;
      }
      let fieldType = getIn([field.name, 'inputType'], fieldDefinitions) || getIn([field.type, 'inputType'], fieldDefinitions) || field.displayType || field.type;
      if (operator.constructor.inputType === RangeInputType && operator.constructor.displayType === DisplayTypes.MomentDate) {
        return FilterOperatorDateRangeInputMoment;
      }
      if (operator.constructor.inputType === RangeInputType && (fieldType === DisplayTypes.DateDisplayType || fieldType === DisplayTypes.DatetimeDisplayType)) {
        return FilterOperatorDateRangeInput;
      }
      const shouldUseEnumInput = shouldUseEnumComponentsForExternalOptionsField(field, draftCondition, filterFamily, getOperatorsForType, getIsUngated);
      if (shouldUseEnumInput) {
        return FilterOperatorEnumInput;
      }

      // display properties of type "number" with referencedObjectType specified
      // as "enumeration" input
      if (getIsUngated('CRM:Filters:ReferencedEnumInput') && fieldType === PropertyTypes.NUMBER && referencedObjectType) {
        fieldType = PropertyTypes.ENUMERATION;
      }
      if (fieldType === DisplayTypes.StringDisplayType && [WildCardEqual, NotWildCardEqual].includes(operator.constructor)) {
        return FilterOperatorElasticsearchTextQueryInput;
      }

      // HACK: This is for a special case in ListSegClassic and should not be
      // relied upon to remain the same.
      if (fieldType === DisplayTypes.StringDisplayType && operator.constructor.isIterableField('value')) {
        return FilterOperatorMultiStringInput;
      }
      switch (fieldType) {
        case DisplayTypes.BooleanDisplayType:
          return FilterOperatorBoolInput;
        case DisplayTypes.DateDisplayType:
        case DisplayTypes.DatetimeDisplayType:
          return operator.constructor.displayType === DisplayTypes.MomentDate ? FilterOperatorDateInputMoment : FilterOperatorDateInput;
        case DisplayTypes.EnumerationDisplayType:
        case SITE_CONTENT:
        case SURVEY_QUESTION:
        case UnifiedEventPropertyTypes.OBJECT_COORDINATES:
          return FilterOperatorEnumInput;
        case EMAIL_SUBSCRIPTION:
          return getIsUngated('subscriptions:groupSettings') ? FilterOperatorEmailSubscriptionInput : FilterOperatorEnumInput;
        case DisplayTypes.NumberDisplayType:
          return FilterOperatorNumberInput;
        case DisplayTypes.PercentageDisplayType:
          return FilterOperatorPercentageInput;
        default:
          return FilterOperatorTextInput;
      }
    };
    this.getReferencedObjectType = (filterFamily, operator) => {
      const {
        baseFilterFamily,
        getFieldDefinitions,
        getReferenceType,
        getReferenceTypeForOperator
      } = this.props;
      const {
        name
      } = operator.field;
      const fieldDefinitions = getFieldDefinitions(filterFamily);
      return getIn([name, 'referencedObjectType'], fieldDefinitions) || getReferenceTypeForOperator(operator, filterFamily || baseFilterFamily) || getIn(['field', 'referencedObjectType'], operator) || getReferenceType(filterFamily || baseFilterFamily, name) || null;
    };
    this.getSpecialOptionsForReferenceType = referenceType => {
      const {
        getSpecialFieldOptions
      } = this.props;
      const specialOptionsByReferenceType = getSpecialFieldOptions();
      if (Object.prototype.hasOwnProperty.call(specialOptionsByReferenceType, referenceType)) {
        const getOptions = specialOptionsByReferenceType[referenceType];
        return getOptions();
      }
      return undefined;
    };
    this.getPlaceholder = operator => {
      return operator.field.placeholder || '';
    };
    this.getLabelString = (field, filterFamily) => {
      const {
        name
      } = field || {};
      const {
        getFieldDefinitions
      } = this.props;
      const {
        filterFamily: filterFamilyFromState
      } = this.state;
      const fieldDefinitions = getFieldDefinitions(filterFamily || filterFamilyFromState);
      const getter = getIn([name, 'getLabelString'], fieldDefinitions) || PropertyDisplay.getPropertyLabel;
      return getter(field);
    };
    this.getDefaultTimezone = field => {
      const {
        hideTimezoneToggle
      } = this.props;
      if (hideTimezoneToggle || !field || MissingField.isMissingField(field)) {
        return undefined;
      }
      return field.get('type') === DATE_TIME ? I18n.moment.portalTz().tz() : 'UTC';
    };
    this.getDescriptionString = field => {
      const {
        description,
        name
      } = field || {};
      const {
        getFieldDefinitions
      } = this.props;
      const {
        filterFamily
      } = this.state;
      const fieldDefinitions = getFieldDefinitions(filterFamily);
      const getDescriptionString = getIn([name, 'getDescriptionString'], fieldDefinitions);
      return getDescriptionString ? getDescriptionString(field) : FieldTranslator.getTranslatedFieldDescription({
        fieldName: name,
        fieldDescription: description,
        filterFamily
      });
    };
    this.getOperatorLabelString = (Operator, field) => {
      return Operator.getLabel ? Operator.getLabel(field) : unescapedText(getOperatorLabelTranslationKey(Operator, field));
    };
    this.getEffectiveFilterFamily = ({
      constraint,
      baseFilterFamily,
      filterFamily
    }) => {
      return constraint ? filterFamily || baseFilterFamily : filterFamily;
    };
    this.getFilterFamilyAndButtonTooltip = filterFamily => {
      const {
        getFilterFamilyAndButtonTooltip,
        getFilterFamilyObjectName
      } = this.props;
      if (getFilterFamilyAndButtonTooltip) {
        return getFilterFamilyAndButtonTooltip(filterFamily, getFilterFamilyObjectName);
      }
      return FilterFamilyAndButtonTooltipTranslator(filterFamily, getFilterFamilyObjectName);
    };
    this.getFilterFamilyGroupHeading = filterFamily => {
      const {
        getFilterFamilyGroupHeading,
        getFilterFamilyObjectName
      } = this.props;
      if (getFilterFamilyGroupHeading) {
        return getFilterFamilyGroupHeading(filterFamily, getFilterFamilyObjectName);
      }
      return FilterFamilyGroupHeadingTranslator(filterFamily, getFilterFamilyObjectName);
    };
    this.getFilterFamilyName = filterFamily => {
      const {
        getFilterFamilyName,
        getFilterFamilyObjectName
      } = this.props;
      if (getFilterFamilyName) {
        return getFilterFamilyName(filterFamily, getFilterFamilyObjectName);
      }
      return FilterFamilyNameTranslator(filterFamily, getFilterFamilyObjectName);
    };
    this.getOperators = (field, contextualFilterFamily) => {
      const {
        constraint,
        getFieldDefinitions,
        getOperatorsForType,
        baseFilterFamily,
        getIsUngated
      } = this.props;
      const {
        draftCondition
      } = this.state;

      // When the filterEditorOperatorDisplay uses this, the state does not have
      // the filterFamily set, but the component knows the filter family
      const filterFamily = this.state.filterFamily || contextualFilterFamily;
      const effectiveFilterFamily = this.getEffectiveFilterFamily({
        constraint,
        filterFamily,
        baseFilterFamily
      });
      const shouldUseEnumOperators = shouldUseEnumComponentsForExternalOptionsField(field, draftCondition, filterFamily, getOperatorsForType, getIsUngated);
      if (shouldUseEnumOperators) {
        const enumOperators = getOperatorsForType(PropertyTypes.ENUMERATION, filterFamily, getIsUngated);
        return enumOperators;
      }
      const fieldDefinitions = getFieldDefinitions(effectiveFilterFamily);
      const fieldDefinitionsPath = [field.name, 'operators'];
      const operatorsByFieldDefinition = getIn(fieldDefinitionsPath, fieldDefinitions);
      if (operatorsByFieldDefinition) {
        return operatorsByFieldDefinition;
      }
      const referencedObjectTypeOperators = getOperatorsForType(field.referencedObjectType, filterFamily, this.props.getIsUngated);
      const fieldTypeOperators = getOperatorsForType(field.type, filterFamily, this.props.getIsUngated);
      const operators = referencedObjectTypeOperators || fieldTypeOperators;
      invariant(operators, `No operator available for field ${field.name}`);
      return operators;
    };
    this.getValueResolver = (filterFamily, operator) => {
      const {
        getReferenceResolvers
      } = this.props;
      if (isExternalOptionsField(operator.field, filterFamily) && operatorSupportsExternalOptions(operator)) {
        // Some usages of the filter still use the old object type constants as a
        // filter family. reference-resolvers-lite requires objectTypeId. This just
        // normalizes the filter family to be an objectTypeId, if the filter family
        // isn't an object type it does nothing.
        const normalizedFilterFamily = ObjectTypesToIds[filterFamily] || filterFamily;
        return getPropertyResolver({
          property: operator.field,
          objectTypeId: normalizedFilterFamily
        });
      }
      const referenceResolvers = getReferenceResolvers(filterFamily);
      const key = this.getReferencedObjectType(filterFamily, operator);
      return getIn([key], referenceResolvers);
    };
    this.getTeamConstraint = ({
      getIsUngated,
      constraint
    }) => {
      return getIsUngated && getIsUngated('teams-partitioning-lists') ? constraint : undefined;
    };
    this.handleChangeFilterFamily = evt => {
      const {
        target: {
          value
        }
      } = evt;
      const {
        getFields,
        onChangeFilterFamily
      } = this.props;
      const fields = getFields(value);
      let defaultValue;
      if (fields && fields.size === 1) {
        const field = fields.first();
        const isFieldSelectSkipped = getIn(['metadata', 'hasNoFieldOptions'], field);
        if (isFieldSelectSkipped) {
          const Operator = this.getOperators(field).first();
          defaultValue = Operator.of(field);
        }
      }
      this.setState({
        filterFamily: value,
        defaultValue,
        initialFilterFamily: undefined
      });
      onChangeFilterFamily(value ? evt : syntheticEvent(undefined));
    };
    this.handlePublicPrimaryVsAnyAssociationChange = (branchPath, assnValue) => {
      const {
        onChange,
        value
      } = this.props;
      const {
        id,
        category
      } = parseAssociationOption(assnValue);
      const nextValue = value.mergeIn(LogicGroup.getRealPath(branchPath), {
        associationTypeId: id,
        associationCategory: category
      });
      onChange(nextValue);
    };
    this.handleOpenCreate = (applyDraftToGroup, getGroup, filterFamily) => {
      this.handleChangeFilterFamily(syntheticEvent(filterFamily));
      const nextState = {
        applyDraftToGroup,
        filterFamily,
        getGroup,
        panelKey: PANEL_KEYS.CREATE,
        nullIssueRelatedState: Object.assign({}, this.state.nullIssueRelatedState, {
          conditionBranchPath: List([PANEL_KEYS.CREATE])
        }),
        isEditMode: false
      };
      this.setState(this.getDerivedState(this.props, nextState));
    };
    this.handleOpenEdit = (keyPath, filterFamily) => {
      const {
        value
      } = this.props;
      const nextState = {
        panelKey: PANEL_KEYS.EDIT,
        draftCondition: LogicGroup.getKeyPath(keyPath, value),
        applyDraftToGroup: this.partial(LogicGroup.setKeyPath, keyPath),
        nullIssueRelatedState: Object.assign({}, this.state.nullIssueRelatedState, {
          conditionBranchPath: keyPath.pop()
        }),
        isEditMode: true
      };
      this.setState(this.getDerivedState(this.props, nextState));
      this.handleChangeFilterFamily(syntheticEvent(filterFamily));
    };
    this.handleIncludeObjectsWithNoAssociatedObjectsChange = includeObjectsWithNoAssociatedObjects => {
      const {
        onDraftChange
      } = this.props;
      const {
        nullIssueRelatedState: {
          conditionBranchPath
        },
        draftValue
      } = this.state;
      const nextValue = draftValue.setIn(LogicGroup.getRealPath(conditionBranchPath).push('includeObjectsWithNoAssociatedObjects'), includeObjectsWithNoAssociatedObjects);
      this.setState({
        draftValue: nextValue
      });
      if (typeof onDraftChange === 'function') {
        onDraftChange(nextValue);
      }
    };
    this.handleDraftOperatorChange = evt => {
      const {
        target: {
          value
        }
      } = evt;
      if (value) {
        this.setState({
          draftCondition: value,
          draftConditionChanged: true
        });
      } else {
        this.setState(prevState => {
          if (prevState.defaultValue) {
            this.handleChangeFilterFamily(syntheticEvent(undefined));
          }
          return {
            defaultValue: value,
            draftCondition: value,
            draftConditionChanged: true
          };
        });
      }
      const {
        onDraftChange,
        onUserAction
      } = this.props;
      const {
        applyDraftToGroup,
        filterFamily,
        originalValue,
        draftValue,
        panelKey,
        isEditMode
      } = this.state;
      if (typeof onDraftChange === 'function') {
        const nextValue = value ? conditionsGrouping.applyConditionToGroup(filterFamily, value, draftValue, applyDraftToGroup, this.state.getGroup, isEditMode) : originalValue;

        // update draftValue only on "edit", on "create" leave untouched
        if (isEditMode) {
          this.setState({
            draftValue: nextValue
          });
        }
        onDraftChange(nextValue);
      }
      if (typeof onUserAction === 'function') {
        onUserAction(FilterUserAction({
          condition: get('name', value),
          filterFamily,
          subAction: CONDITION_DRAFT_CHANGED,
          panelKey
        }));
      }
    };
    this.handleDraftOperatorDone = (evt, isAborted = false) => {
      const {
        target: {
          value: nextCondition
        }
      } = evt;
      const {
        onChange,
        onOperatorChangeConfirmed,
        onUserAction
      } = this.props;
      const {
        applyDraftToGroup,
        draftConditionChanged,
        originalValue,
        filterFamily,
        panelKey,
        draftValue,
        isEditMode
      } = this.state;
      let nextValue;
      // if user clicked "back" reset to originalValue, otherwise use mutated draftValue
      if (isAborted) {
        nextValue = nextCondition ? applyDraftToGroup(nextCondition, originalValue, filterFamily) : originalValue;
      } else {
        nextValue = nextCondition ? conditionsGrouping.applyConditionToGroup(filterFamily, nextCondition, draftValue, applyDraftToGroup, this.state.getGroup, isEditMode) : originalValue;
      }
      if (!isAborted || isAborted && draftConditionChanged) {
        onChange(nextValue);
        if (typeof onOperatorChangeConfirmed === 'function') {
          onOperatorChangeConfirmed(panelKey, filterFamily, nextCondition);
        }
      }
      const state = {
        draftConditionChanged: false,
        filterFamily: undefined,
        panelKey: PANEL_KEYS.LIST
      };
      this.setState(this.getDerivedState(this.props, state));
      if (typeof onUserAction === 'function') {
        onUserAction(FilterUserAction({
          condition: get('name', nextCondition),
          filterFamily,
          refinement: getIn(['refinement', 'name'], nextCondition),
          subAction: isAborted ? CONDITION_DRAFT_SAVED : CONDITION_DRAFT_ABANDONED,
          panelKey
        }));
      }
    };
    this.handleOperatorDoneCreate = evt => {
      const {
        onUserAction
      } = this.props;
      const {
        filterFamily,
        panelKey
      } = this.state;
      this.handleDraftOperatorDone(evt);
      const {
        onOperatorConfirmed
      } = this.props;
      if (typeof onOperatorConfirmed === 'function') {
        onOperatorConfirmed(evt);
      }
      if (typeof onUserAction === 'function') {
        onUserAction(FilterUserAction({
          condition: getIn(['target', 'value', 'name'], evt),
          filterFamily,
          panelKey,
          refinement: getIn(['target', 'value', 'refinement', 'name'], evt),
          subAction: CONDITION_ADDED
        }));
      }
    };
    this.handleOperatorClone = insertClone => {
      const {
        onChange,
        onUserAction,
        value
      } = this.props;
      const {
        panelKey
      } = this.state;
      const nextValue = typeof insertClone === 'function' ? insertClone(value) : undefined;
      onChange(nextValue);
      if (typeof onUserAction === 'function') {
        onUserAction(FilterUserAction({
          subAction: CONDITION_CLONED,
          panelKey
        }));
      }
    };
    this.handleOperatorRemove = keyPath => {
      const {
        onChange,
        onUserAction,
        value
      } = this.props;
      const {
        panelKey
      } = this.state;
      const nextValue = LogicGroup.removeKeyPath(keyPath, value);
      onChange(nextValue);
      if (typeof onUserAction === 'function') {
        onUserAction(FilterUserAction({
          subAction: CONDITION_DELETED,
          panelKey
        }));
      }
    };
    this.handleShowAllConditionsClick = () => {
      const {
        onUserAction
      } = this.props;
      const {
        panelKey
      } = this.state;
      if (typeof onUserAction === 'function') {
        onUserAction(FilterUserAction({
          subAction: SHOW_ALL_CONDITIONS_CLICKED,
          panelKey
        }));
      }
    };
    this.renderFieldLink = ({
      url,
      value
    }) => {
      const {
        FieldLinkComponent,
        onUserAction
      } = this.props;
      const {
        filterFamily,
        panelKey
      } = this.state;
      if (!url) {
        return null;
      }
      const fieldLinkProps = {
        filterFamily,
        onUserAction,
        panelKey,
        url,
        value
      };
      return /*#__PURE__*/_jsx(FieldLinkComponent, Object.assign({}, fieldLinkProps));
    };
    this.state = Object.assign({
      draftCondition: null,
      draftConditionChanged: false,
      /**
       *
       * HACK(@sdesota): Using this can break some edge cases
       *
       * When we begin editing a filter, we set `originalValue` to the current
       * value. If `back` is clicked, we revert to the originalValue.
       *
       * If:
       *
       * 1. User starts editing filter
       * 2. The parent changes the filters independently of the filter editor
       * 3. The user clicks back
       *
       * Now the parent would receive a `onChange` event with the original value
       * before the draft was created, ignoring its own changes.
       *
       * Eventually, we should find a better pattern.
       *
       */
      originalValue: null,
      draftValue: null
    }, this.getDerivedState(_props), {
      // "null association" and "null values" related state
      nullIssueRelatedState: {
        conditionBranchPath: List([PANEL_KEYS.CREATE]),
        showIncludeObjectsWithNoValueSetByOperator: {},
        showIncludeObjectsWithNoAssociatedObjectsByFilterBranch: {},
        defaultNullValueByOperator: {}
      },
      isEditMode: undefined
    });

    // In new code, please use ES6 spread args rather than partials
    // for easier debugging and readability:
    // (...args) => myFunction('partiallyAppliedParam', ...args)
    this.partial = memoize(partial);
  }
  componentDidMount() {
    const {
      isInitialScreenCreate,
      onEditingChange
    } = this.props;
    const {
      panelKey
    } = this.state;
    if (typeof onEditingChange === 'function' && panelKey === PANEL_KEYS.CREATE && isInitialScreenCreate) {
      onEditingChange(syntheticEvent(true));
    }
    if (this.state.initialFilterFamily) {
      this.handleChangeFilterFamily(syntheticEvent(this.state.initialFilterFamily));
    }
  }
  shouldComponentUpdate(nextProps, nextState) {
    return _isImmutableEqual(this.props, nextProps) || _isImmutableEqual(this.state, nextState);
  }
  componentDidUpdate(prevProps, prevState) {
    const {
      isInitialScreenCreate,
      onEditingChange
    } = prevProps;
    const {
      value,
      isReadOnly
    } = this.props;
    const {
      draftCondition,
      panelKey
    } = this.state;
    const {
      didScheduleStepInitializationCheck
    } = this.state;
    const getConditionCount = logicGroup => {
      const conditions = getIn(['conditions'], logicGroup) || List();
      return conditions.size;
    };
    const isConditionCountEmpty = getConditionCount(value) < 1 && getConditionCount(draftCondition) < 1;
    const isStepInitializationCheckNeededThisTick = didScheduleStepInitializationCheck || panelKey === PANEL_KEYS.LIST && isInitialScreenCreate;
    if (isStepInitializationCheckNeededThisTick && value !== prevProps.value && isConditionCountEmpty) {
      const addToRoot = partial(LogicGroup.addToKeyPath, List());
      this.handleOpenCreate(addToRoot, identity, undefined);
    }
    if (prevState.panelKey && panelKey !== prevState.panelKey && typeof onEditingChange === 'function') {
      const isEditing = panelKey !== PANEL_KEYS.LIST;
      onEditingChange(syntheticEvent(isEditing));
    }
    if (isReadOnly && panelKey !== PANEL_KEYS.LIST) {
      this.setState({
        panelKey: PANEL_KEYS.LIST
      });
    }
  }
  defaultOperatorDescription({
    children
  }) {
    return /*#__PURE__*/_jsx(View, {
      as: "div",
      paddingInlineStart: "400",
      children: children
    });
  }
  render() {
    const _this$props = this.props,
      {
        DescriptionListComponent,
        OperatorDescriptionComponent,
        addConditionDisabledTooltip,
        bannerComponents,
        baseFilterFamily,
        className,
        constraint,
        currencyCode,
        customRestrictedFiltersViewState,
        displayMode,
        editorSubtitle,
        getActiveFieldNames,
        getFieldDefinitions,
        getFieldGroups,
        getFields,
        getFilterFamilyHasEntities,
        getFilterFamilyObjectName,
        getFilterFamilyOptions,
        getInitialConditionCountCutoff,
        getIsUngated,
        getObjectPropertyLabel,
        isAddConditionDisabled,
        isFieldDescriptionVisible,
        isFiscalYearEnabled,
        isInitialScreenCreate,
        isReadOnly,
        isRollingDateOffsetInputEnabled,
        isXoEnabled,
        labelOperatorCreateSave,
        labelOperatorEditSave,
        matchStatus,
        onFieldSearchChange,
        onTempColumnAdd,
        onUserAction,
        searchable,
        showIncludeUnknownValues,
        showInvalidOptionErrors,
        showInitialStepBackButton,
        style,
        validateOperator,
        value,
        valueBase,
        getObjectAssociationOptions,
        isAssociationSelectEnabled
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const {
      defaultValue,
      draftCondition,
      nullIssueRelatedState: {
        conditionBranchPath
      },
      draftValue,
      filterFamily,
      getGroup,
      panelKey
    } = this.state;
    const {
      getOperatorsForType
    } = this.props;
    const filterBranch = draftValue && conditionBranchPath ? LogicGroup.getKeyPath(conditionBranchPath, draftValue) : null;
    const dataAttributes = Object.keys(rest).filter(key => key.startsWith('data-')).reduce((acc, key) => {
      acc[key] = rest[key];
      return acc;
    }, {});
    const activeGroup = Or.isOr(value) ? getGroup(value || {}) : value;
    const activeFieldNames = getActiveFieldNames(filterFamily)(activeGroup).union(getGroup(value) === value ? ImmutableSet() : _getActiveFieldNamesFromGroup(activeGroup || {}));
    const safeConstraint = this.getTeamConstraint({
      getIsUngated,
      constraint
    });
    const isOperatorIncompatibleWithFieldType = isOperatorIncompatibleWithExternalOptionsField(draftCondition, filterFamily, getOperatorsForType, getIsUngated);
    const canFilterBeConverted = isOperatorIncompatibleWithFieldType && typeof attemptToConvertExternalOptionsFilter(draftCondition) !== 'undefined';
    const editAndCreateSharedProps = Object.assign({}, rest, {
      activeFieldNames,
      activeGroup,
      className,
      constraint: safeConstraint,
      currencyCode,
      draftCondition,
      editorSubtitle,
      getDefaultTimezone: this.getDefaultTimezone,
      fields: getFields(filterFamily),
      fieldGroups: getFieldGroups(filterFamily),
      filterFamily,
      isRollingDateOffsetInputEnabled,
      isFiscalYearEnabled,
      isXoEnabled,
      getDescriptionString: this.getDescriptionString,
      getFamilyValueResolver: this.partial(this.getValueResolver, filterFamily),
      getFilterFamilyHasEntities,
      getFilterFamilyName: this.getFilterFamilyName,
      getFilterFamilyObjectName,
      getObjectPropertyLabel,
      getFilterFamilyGroupHeading: this.getFilterFamilyGroupHeading,
      getInputComponent: this.getInputComponent,
      getIsUngated,
      getLabelString: this.getLabelString,
      getOperatorLabel: this.getOperatorLabelString,
      getOperators: this.getOperators,
      getPlaceholder: this.getPlaceholder,
      getReferencedObjectType: this.getReferencedObjectType,
      getSpecialOptionsForReferenceType: this.getSpecialOptionsForReferenceType,
      isFieldDescriptionVisible,
      onChange: this.handleDraftOperatorChange,
      onFieldSearchChange,
      renderFieldLink: this.renderFieldLink,
      searchable,
      showIncludeUnknownValues,
      style,
      validateOperator,
      nullIssueRelatedState: this.state.nullIssueRelatedState,
      updateNullIssueRelatedState: this.updateNullIssueRelatedState,
      onUserAction
    });
    if (value instanceof Restricted) {
      return customRestrictedFiltersViewState || /*#__PURE__*/_jsx(RestrictedFiltersViewState, {});
    }
    if (displayMode === DESCRIPTION) {
      return /*#__PURE__*/_jsx(FilterEditorOperatorDescriptionList, Object.assign({}, rest, {
        DescriptionListComponent: DescriptionListComponent,
        OperatorDescriptionComponent: typeof OperatorDescriptionComponent === 'function' ? OperatorDescriptionComponent : this.defaultOperatorDescription,
        baseFilterFamily: baseFilterFamily,
        className: className,
        currencyCode: currencyCode,
        getFieldDefinitions: getFieldDefinitions,
        getFilterFamilyGroupHeading: this.getFilterFamilyGroupHeading,
        getIsUngated: getIsUngated,
        getLabelString: this.getLabelString,
        getOperatorLabel: this.getOperatorLabelString,
        getOperators: this.getOperators,
        getReferencedObjectType: this.getReferencedObjectType,
        getSpecialOptionsForReferenceType: this.getSpecialOptionsForReferenceType,
        getValueResolver: this.getValueResolver,
        isXoEnabled: isXoEnabled,
        style: style,
        value: value
      }));
    }
    if (panelKey === PANEL_KEYS.CREATE) {
      const isValueEmpty = value && value.conditions && value.conditions.size === 0;
      return /*#__PURE__*/_jsx(FilterEditorOperatorCreate, Object.assign({}, editAndCreateSharedProps, {
        getFilterFamilyOptions: getFilterFamilyOptions,
        isInitialStepBackButtonDisabled: !showInitialStepBackButton && isInitialScreenCreate && isValueEmpty,
        labelOperatorCreateSave: labelOperatorCreateSave,
        onChangeFilterFamily: this.handleChangeFilterFamily,
        onDone: this.handleOperatorDoneCreate,
        onTempColumnAdd: onTempColumnAdd,
        value: draftCondition || defaultValue
      }));
    } else if (panelKey === PANEL_KEYS.EDIT) {
      return /*#__PURE__*/_jsx(FilterEditorOperatorEdit, Object.assign({}, editAndCreateSharedProps, {
        canFilterBeConverted: canFilterBeConverted,
        filterBranch: filterBranch,
        handleIncludeObjectsWithNoAssociatedObjectsChange: this.handleIncludeObjectsWithNoAssociatedObjectsChange,
        isFilterIncompatible: isOperatorIncompatibleWithFieldType,
        labelOperatorEditSave: labelOperatorEditSave,
        onDone: this.handleDraftOperatorDone,
        value: draftCondition
      }));
    }
    return /*#__PURE__*/_jsx(FilterEditorOperatorDisplayList, Object.assign({}, dataAttributes, {
      addConditionDisabledTooltip: addConditionDisabledTooltip,
      bannerComponents: bannerComponents,
      baseFilterFamily: baseFilterFamily,
      className: className,
      constraint: safeConstraint,
      currencyCode: currencyCode,
      data: this.props.data,
      getFieldDefinitions: getFieldDefinitions,
      getFilterFamilyAndButtonTooltip: this.getFilterFamilyAndButtonTooltip,
      getFilterFamilyGroupHeading: this.getFilterFamilyGroupHeading,
      getFilterFamilyHeading: this.getFilterFamilyGroupHeading,
      getFilterFamilyObjectName: getFilterFamilyObjectName,
      getInitialConditionCountCutoff: getInitialConditionCountCutoff,
      getIsUngated: getIsUngated,
      getLabelString: this.getLabelString,
      getObjectAssociationOptions: getObjectAssociationOptions,
      getOperatorLabel: this.getOperatorLabelString,
      getOperators: this.getOperators,
      getOperatorsForType: getOperatorsForType,
      getReferencedObjectType: this.getReferencedObjectType,
      getSpecialOptionsForReferenceType: this.getSpecialOptionsForReferenceType,
      getValueResolver: this.getValueResolver,
      handleOpenCreate: this.handleOpenCreate,
      handleOpenEdit: this.handleOpenEdit,
      handleOperatorClone: this.handleOperatorClone,
      handleOperatorRemove: this.handleOperatorRemove,
      isAddConditionDisabled: isAddConditionDisabled,
      isAssociationSelectEnabled: isAssociationSelectEnabled,
      isReadOnly: isReadOnly,
      isXoEnabled: isXoEnabled,
      matchStatus: matchStatus,
      onBranchAssociationChange: this.handlePublicPrimaryVsAnyAssociationChange,
      onShowAllConditionsClick: this.handleShowAllConditionsClick,
      onUserAction: onUserAction,
      showInvalidOptionErrors: showInvalidOptionErrors,
      style: style,
      validateOperator: validateOperator,
      value: value,
      valueBase: valueBase
    }));
  }
}
XOFilterEditor.defaultProps = {
  DescriptionListComponent: UIList,
  FieldLinkComponent: FieldLink,
  FieldSelectItemComponent: DefaultFieldSelectItemComponent,
  currencyCode: 'USD',
  displayMode: EDITOR,
  getActiveFieldNames: always(always(ImmutableSet())),
  getFieldDefinitions: always(ImmutableMap),
  getFieldGroups: always(undefined),
  getFilterFamilyHasEntities: always(false),
  getFilterFamilyObjectName: LegacyFilterFamilyObjectNameTranslator,
  getFilterFamilyOptions: always(undefined),
  getInitialConditionCountCutoff: identity,
  getIsUngated: always(false),
  getObjectPropertyLabel: always((assetFamily, propertyName) => propertyName),
  getOperatorsForType: getObjectSegOperatorsForTypeStrict,
  getReferenceResolvers: always(ImmutableMap()),
  getReferenceType: always(null),
  getReferenceTypeForOperator: always(undefined),
  getSpecialFieldOptions: getSpecialOptionsByReferenceType,
  isAddConditionDisabled: false,
  isAssociationSelectEnabled: false,
  isFieldDescriptionVisible: false,
  isFieldUrlVisible: false,
  isFiscalYearEnabled: false,
  isRollingDateOffsetInputEnabled: false,
  onChangeFilterFamily: identity,
  onDraftChange: identity,
  onFieldSearchChange: identity,
  searchable: true,
  showIncludeUnknownValues: false,
  showInitialStepBackButton: false,
  showInvalidOptionErrors: false,
  validateOperator: always(FilterOperatorErrorRecord({
    error: false
  }))
};
XOFilterEditor.propTypes = {
  DescriptionListComponent: PropTypes.elementType.isRequired,
  FieldLinkComponent: PropTypes.elementType,
  FieldSelectItemComponent: PropTypes.elementType.isRequired,
  OperatorDescriptionComponent: PropTypes.elementType,
  addConditionDisabledTooltip: PropTypes.node,
  bannerComponents: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]),
  baseFilterFamily: PropTypes.string.isRequired,
  className: PropTypes.string,
  constraint: PropTypes.instanceOf(Constraint),
  currencyCode: PropTypes.string.isRequired,
  customRestrictedFiltersViewState: PropTypes.element,
  data: PropTypes.instanceOf(ImmutableMap),
  displayMode: PropTypes.oneOf([DESCRIPTION, EDITOR]),
  editorSubtitle: PropTypes.node,
  getActiveFieldNames: PropTypes.func.isRequired,
  getFieldDefinitions: PropTypes.func.isRequired,
  getFieldGroups: PropTypes.func,
  getFields: PropTypes.func.isRequired,
  getFilterFamilyAndButtonTooltip: PropTypes.func,
  getFilterFamilyGroupHeading: PropTypes.func,
  getFilterFamilyHasEntities: PropTypes.func,
  getFilterFamilyName: PropTypes.func,
  getFilterFamilyObjectName: PropTypes.func.isRequired,
  getFilterFamilyOptions: PropTypes.func.isRequired,
  getInitialConditionCountCutoff: PropTypes.func,
  getIsUngated: PropTypes.func.isRequired,
  getObjectAssociationOptions: PropTypes.func,
  getObjectPropertyLabel: PropTypes.func.isRequired,
  getOperatorsForType: PropTypes.func.isRequired,
  getReferenceResolvers: PropTypes.func.isRequired,
  getReferenceType: PropTypes.func.isRequired,
  getReferenceTypeForOperator: PropTypes.func,
  getSpecialFieldOptions: PropTypes.func.isRequired,
  hideTimezoneToggle: PropTypes.bool,
  isAddConditionDisabled: PropTypes.bool,
  isAssociationSelectEnabled: PropTypes.bool,
  isFieldDescriptionVisible: PropTypes.bool,
  isFiscalYearEnabled: PropTypes.bool,
  isInitialScreenCreate: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  isRollingDateOffsetInputEnabled: PropTypes.bool.isRequired,
  isXoEnabled: PropTypes.bool,
  labelOperatorCreateSave: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  labelOperatorEditSave: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  loadMoreFields: PropTypes.func,
  matchStatus: PropTypes.arrayOf(MatchStatusType),
  onChange: PropTypes.func,
  onChangeFilterFamily: PropTypes.func,
  onDraftChange: PropTypes.func.isRequired,
  onEditingChange: PropTypes.func,
  onFieldSearchChange: PropTypes.func,
  onOperatorChangeConfirmed: PropTypes.func,
  onOperatorConfirmed: PropTypes.func,
  onTempColumnAdd: PropTypes.func,
  onUserAction: PropTypes.func,
  searchable: PropTypes.bool,
  showIncludeUnknownValues: PropTypes.bool.isRequired,
  showInitialStepBackButton: PropTypes.bool,
  showInvalidOptionErrors: PropTypes.bool.isRequired,
  style: PropTypes.object,
  // eslint-disable-line react/forbid-prop-types
  validateOperator: PropTypes.func,
  value: FilterType.isRequired,
  valueBase: FilterType
};